import React, { useEffect, useRef, useState } from 'react';
import { api } from '../api/api';
import { hoyMadrid, textoDetalleApi } from '../utils/fechas';
import { numeroEs } from '../utils/logistica2';

// Factura repartida: el presupuesto lo pagan varios clientes (p. ej. cinco
// ayuntamientos a partes iguales). Una factura por pagador con su parte de cada
// línea; la del cliente del presupuesto es la principal. Backend:
// POST /presupuestos/{id}/facturar-repartido (con `simular` para la vista previa).

const eur = (v) => new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(v || 0);
const nombreCliente = (c) => (c && (c.nombre_fiscal || c.nombre_empresa || c.nombre)) || '';
const fechaTexto = (iso) => {
  const s = String(iso || '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return '';
  const meses = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
  const [y, m, d] = s.split('-').map(Number);
  return `${d} de ${meses[m - 1]} de ${y}`;
};

const filaVacia = (cliente = null) => ({
  clienteId: cliente?.id || '',
  busqueda: nombreCliente(cliente),
  destinos: null,
  destinoId: '',
  porcentaje: '',
  pedido: '',
  observacion: '',
});

export default function FacturaRepartida({ presupuesto, cliente, todosClientes, cargarTodosClientes, onEmitido }) {
  const [abierto, setAbierto] = useState(false);
  const [filas, setFilas] = useState([]);
  const [fecha, setFecha] = useState(hoyMadrid());
  const [vista, setVista] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState('');
  const pedidos = useRef(new Set()); // clientes cuyos destinos ya se han pedido

  const abrir = () => {
    setAbierto(true);
    setFilas([filaVacia(cliente), filaVacia()]);
    setFecha(hoyMadrid());
    setVista(null);
    setError('');
    pedidos.current = new Set();
    if (!todosClientes || todosClientes.length === 0) cargarTodosClientes();
  };

  // Destinos FACe de cada cliente elegido (se piden una vez por fila)
  useEffect(() => {
    filas.forEach((f, i) => {
      if (!f.clienteId || f.destinos !== null || pedidos.current.has(`${i}:${f.clienteId}`)) return;
      pedidos.current.add(`${i}:${f.clienteId}`);
      api.get(`/face/clientes/${f.clienteId}/destinos`)
        .then(({ data }) => {
          const destinos = data?.face_destinos || [];
          setFilas((prev) => prev.map((x, j) => (j === i && x.clienteId === f.clienteId
            ? { ...x, destinos, destinoId: x.destinoId || (destinos[0]?.id || '') } : x)));
        })
        .catch(() => setFilas((prev) => prev.map((x, j) => (j === i && x.clienteId === f.clienteId ? { ...x, destinos: [] } : x))));
    });
  }, [filas]);

  const cambiar = (i, cambios) => {
    setVista(null);
    setFilas((prev) => prev.map((f, j) => (j === i ? { ...f, ...cambios } : f)));
  };

  const textoObservacion = (fila, n) => {
    const c = (todosClientes || []).find((x) => x.id === fila.clienteId) || (fila.clienteId === cliente?.id ? cliente : null);
    const pax = Number(presupuesto?.num_personas) || 0;
    const pct = fila.porcentaje ? Number(fila.porcentaje) : 100 / n;
    const paxParte = pax ? Math.round((pax * pct) / 100 * 100) / 100 : 0;
    const fechaEv = fechaTexto(presupuesto?.fecha_evento);
    const lugar = String(presupuesto?.lugar_evento || '').split(',').slice(0, 2).join(',').trim();
    const parte = fila.porcentaje ? `${String(pct).replace('.', ',')} %` : `1 de ${n}`;
    return [
      `Servicio de catering${fechaEv ? ` del ${fechaEv}` : ''}${lugar ? ` en ${lugar}` : ''}${pax ? ` (${pax} personas)` : ''}.`,
      `Presupuesto Duetto ${presupuesto?.numero || ''}.`,
      `Parte correspondiente a ${nombreCliente(c)}${paxParte ? `: ${String(paxParte).replace('.', ',')} de ${pax} personas` : ''} (${parte}).`,
    ].join(' ');
  };

  const cuerpo = (simular) => ({
    simular,
    fecha_emision: fecha || null,
    partes: filas.map((f) => ({
      cliente_id: f.clienteId,
      porcentaje: f.porcentaje === '' ? null : Number(f.porcentaje),
      face_destino_id: f.destinoId || null,
      face_numero_pedido: f.pedido.trim() || null,
      face_observaciones: f.observacion.trim() || textoObservacion(f, filas.length),
    })),
  });

  const validar = () => {
    if (filas.some((f) => !f.clienteId)) return 'Elige el cliente de cada pagador (o quita la fila vacía).';
    if (filas.length < 2) return 'Hacen falta al menos dos pagadores.';
    const conPct = filas.filter((f) => f.porcentaje !== '').length;
    if (conPct && conPct !== filas.length) return 'Pon el porcentaje de todos o de ninguno (partes iguales).';
    return '';
  };

  const verPrevia = async () => {
    const msg = validar();
    if (msg) { setError(msg); return; }
    setCargando(true); setError('');
    try {
      const { data } = await api.post(`/presupuestos/${presupuesto.id}/facturar-repartido`, cuerpo(true));
      setVista(data);
    } catch (e) {
      setError(textoDetalleApi(e.response?.data?.detail, e.message));
    }
    setCargando(false);
  };

  const emitir = async () => {
    if (!vista) return;
    const ok = window.confirm(
      `Se emitirán ${vista.partes.length} facturas (${eur(vista.total_partes)} en total) con números correlativos reales:\n\n` +
      vista.partes.map((p) => `· ${p.cliente_nombre}: ${eur(p.importe_total)}`).join('\n') +
      '\n\n¿Emitirlas?'
    );
    if (!ok) return;
    setCargando(true); setError('');
    try {
      const { data } = await api.post(`/presupuestos/${presupuesto.id}/facturar-repartido`, cuerpo(false));
      alert(`${data.message}${data.aviso ? `\n\n${data.aviso}` : ''}`);
      setAbierto(false);
      onEmitido && onEmitido();
    } catch (e) {
      setError(textoDetalleApi(e.response?.data?.detail, e.message));
    }
    setCargando(false);
  };

  if (!abierto) {
    return (
      <button
        type="button"
        onClick={abrir}
        data-testid="abrir-factura-repartida"
        className="w-full mb-4 px-3 py-2 border-2 border-dashed border-sky-300 text-sky-700 rounded-lg hover:bg-sky-50 text-sm font-medium"
      >
        Repartir la factura entre varios pagadores
      </button>
    );
  }

  return (
    <div className="border-2 rounded-lg p-4 mb-4 bg-sky-50 border-sky-300 space-y-3" data-testid="form-factura-repartida">
      <div className="text-sm font-semibold text-sky-900">Repartir la factura entre varios pagadores</div>
      <p className="text-xs text-sky-800">
        Una factura por pagador con su parte de cada línea (unidades e importe). Sin porcentajes, a partes iguales.
        La de {nombreCliente(cliente) || 'el cliente del presupuesto'} es la principal; las demás quedan como facturas a otra empresa.
        Cada una lleva su destino FACe y su observación.
      </p>

      {filas.map((f, i) => {
        const esCliente = f.clienteId && f.clienteId === cliente?.id;
        return (
          <div key={i} className="bg-white border border-sky-200 rounded-lg p-3 space-y-2" data-testid={`reparto-fila-${i}`}>
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-sky-800">Pagador {i + 1}{esCliente ? ' · factura principal' : ''}</span>
              {!esCliente && filas.length > 2 && (
                <button type="button" onClick={() => { setVista(null); setFilas((p) => p.filter((_, j) => j !== i)); }}
                  className="text-xs text-red-600 hover:underline">Quitar</button>
              )}
            </div>
            {esCliente ? (
              <div className="text-sm font-medium text-gray-900">{nombreCliente(cliente)}</div>
            ) : (
              <div>
                <input
                  type="text"
                  value={f.busqueda}
                  onChange={(e) => cambiar(i, { busqueda: e.target.value, clienteId: '', destinos: null, destinoId: '' })}
                  placeholder="Buscar cliente por nombre o CIF..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
                {!f.clienteId && f.busqueda.trim().length >= 2 && (
                  <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-lg bg-white mt-1">
                    {(todosClientes || [])
                      .filter((c) => !filas.some((x) => x.clienteId === c.id))
                      .filter((c) => {
                        const q = f.busqueda.trim().toLowerCase();
                        return [c.nombre_empresa, c.nombre_fiscal, c.nombre, c.cif, c.nif].some((v) => v && String(v).toLowerCase().includes(q));
                      })
                      .slice(0, 20)
                      .map((c) => (
                        <button key={c.id} type="button"
                          onClick={() => cambiar(i, { clienteId: c.id, busqueda: nombreCliente(c), destinos: null, destinoId: '' })}
                          className="block w-full text-left px-3 py-1.5 text-sm hover:bg-sky-50">
                          {nombreCliente(c)}
                          <span className={`ml-2 text-xs ${(c.cif || c.nif) ? 'text-gray-500' : 'text-red-600'}`}>{c.cif || c.nif || 'sin CIF'}</span>
                        </button>
                      ))}
                  </div>
                )}
              </div>
            )}
            {f.clienteId && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <div className="sm:col-span-2">
                  <label className="block text-xs text-gray-600 mb-1">Destino FACe</label>
                  {f.destinos === null ? (
                    <div className="text-xs text-gray-500 py-2">Cargando...</div>
                  ) : f.destinos.length === 0 ? (
                    <div className="text-xs text-gray-500 py-2">Sin destinos FACe (factura normal)</div>
                  ) : (
                    <select value={f.destinoId} onChange={(e) => cambiar(i, { destinoId: e.target.value })}
                      className="w-full px-2 py-2 border border-gray-300 rounded-lg text-sm">
                      {f.destinos.map((d) => (
                        <option key={d.id} value={d.id}>
                          {(d.nombre_interno || d.nombre || 'Destino')} · {d.oficina_contable}/{d.organo_gestor}/{d.unidad_tramitadora}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">% (vacío = igual)</label>
                  <input type="number" min="0" max="100" step="0.01" value={f.porcentaje}
                    onChange={(e) => cambiar(i, { porcentaje: e.target.value })}
                    className="w-full px-2 py-2 border border-gray-300 rounded-lg text-sm" />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Nº de pedido</label>
                  <input type="text" value={f.pedido} onChange={(e) => cambiar(i, { pedido: e.target.value })}
                    className="w-full px-2 py-2 border border-gray-300 rounded-lg text-sm" />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-xs text-gray-600 mb-1">Observación (vacío = automática)</label>
                  <input type="text" value={f.observacion} onChange={(e) => cambiar(i, { observacion: e.target.value })}
                    placeholder={textoObservacion(f, filas.length)}
                    className="w-full px-2 py-2 border border-gray-300 rounded-lg text-sm" />
                </div>
              </div>
            )}
          </div>
        );
      })}

      <button type="button" onClick={() => { setVista(null); setFilas((p) => [...p, filaVacia()]); }}
        className="text-sm text-sky-700 hover:underline">+ Añadir pagador</button>

      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">Fecha de emisión (todas)</label>
        <input type="date" value={fecha} onChange={(e) => { setVista(null); setFecha(e.target.value); }}
          className="w-full sm:w-48 px-3 py-2 border border-gray-300 rounded-lg text-sm" />
      </div>

      {error && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2">{error}</div>}

      {vista && (
        <div className="bg-white border border-sky-200 rounded-lg p-3 space-y-3" data-testid="reparto-vista-previa">
          <div className="text-sm font-semibold text-gray-900">
            Vista previa: {vista.partes.length} facturas · {eur(vista.total_partes)} (presupuesto {eur(vista.total_presupuesto)})
          </div>
          {vista.aviso && <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">{vista.aviso}</div>}
          {vista.partes.map((p, i) => (
            <div key={i} className="border-t border-gray-100 pt-2 text-xs text-gray-700">
              <div className="flex justify-between text-sm">
                <span className="font-medium text-gray-900">{p.cliente_nombre} <span className="text-gray-500 font-normal">{p.cliente_nif}</span>{p.principal ? ' · principal' : ''}</span>
                <span className="font-semibold">{eur(p.importe_total)}</span>
              </div>
              {p.face_destino ? (
                <div>FACe {p.face_destino.nombre}: {p.face_destino.oficina_contable} / {p.face_destino.organo_gestor} / {p.face_destino.unidad_tramitadora}{p.face_destino.organo_proponente ? ` / ${p.face_destino.organo_proponente}` : ''}</div>
              ) : <div>Sin FACe</div>}
              {(p.lineas || []).map((l, j) => (
                <div key={j}>{numeroEs(l.unidades, 2)} x {l.concepto_cliente || l.concepto} a {eur(l.precio_unitario)} = {eur(l.base_imponible)} + IVA {l.tipo_impositivo} %</div>
              ))}
              <div>Base {eur(p.base_imponible)} · IVA {eur(p.cuota)}</div>
              {p.face_numero_pedido && <div>Pedido: {p.face_numero_pedido}</div>}
              <div className="italic text-gray-500">{p.face_observaciones}</div>
            </div>
          ))}
        </div>
      )}

      <div className="flex gap-2">
        {!vista ? (
          <button type="button" onClick={verPrevia} disabled={cargando} data-testid="reparto-previa"
            className="flex-1 px-3 py-2 bg-sky-600 text-white rounded-lg hover:bg-sky-700 text-sm font-medium disabled:opacity-50">
            {cargando ? 'Calculando...' : 'Vista previa'}
          </button>
        ) : (
          <button type="button" onClick={emitir} disabled={cargando} data-testid="reparto-emitir"
            className="flex-1 px-3 py-2 bg-duetto-primary text-white rounded-lg hover:opacity-90 text-sm font-medium disabled:opacity-50">
            {cargando ? 'Emitiendo...' : `Emitir ${vista.partes.length} facturas`}
          </button>
        )}
        <button type="button" onClick={() => setAbierto(false)}
          className="px-3 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium">
          Cancelar
        </button>
      </div>
    </div>
  );
}
